/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */
import { FC, useEffect, useMemo, useState } from 'react'

import { useApi } from '@/hooks'
import logger from '@/utils/logger'

import { useStatusColumnsContext } from './StatusColumnsContext'

import { useMapContext } from '../MapContext'
import { StatusColumn } from '../types'

type StatusColumnsSelectorProps = {
  onClose: () => void
}

const columnCategoryMap: Record<string, string[]> = {
  Manifest: [
    'customer',
    'loadNumber',
    'loadSummary',
    'despatchDate',
    'originLocation',
    'numberOfPickups',
    'destination',
    'deliveryDate',
    'numberOfDrops',
    'driverId',
    'driverName',
  ],

  Zones: [
    'zone1Mode',
    'zone1ReturnAir',
    'zone1SupplyAir',
    'zone1SetPoint',
    'zone1VirtualSetpoint',
    'zone2Mode',
    'zone2ReturnAir',
    'zone2SupplyAir',
    'zone2SetPoint',
    'zone2VirtualSetpoint',
    'zone3Mode',
    'zone3ReturnAir',
    'zone3SupplyAir',
    'zone3SetPoint',
    'zone3VirtualSetpoint',
  ],

  Location: [
    'bearingDegrees',
    'city',
    'geofence',
    'state',
    'street',
    'postcode',
    'addressShort',
    'country',
    'lastKnownAddress',
    'lastKnownGeofence',
  ],

  Monitoring: ['tempMpSummary', 'doorMpSummary'],

  Standard: [
    'airControl',
    'ambientTemperatureCelsius',
    'speedKmsHr',
    'batteryVolts',
    'comments',
    'compressorHours',
    'umd',
    'dieselHourstillnextservice',
    'dieselHours',
    'electricHours',
    'engineSpeed',
    'stopSampleTime',
    'twowayCommandsState',
    'maxSpeedKmsHr',
    'calibrationDays',
    'fridgeMake',
    'fridgeModel',
    'source',
    'totalHours',
  ],

  Status: ['fuelPercent', 'operation', 'powerMode', 'powerSource', 'fridgeProfile', 'offlineSince'],
}

const findCategoryFor = (property: string) => {
  const toRegexAndMatch = (term: string) => {
    return new RegExp(term).test(property)
  }

  const match = Object.keys(columnCategoryMap).find((k) => {
    return columnCategoryMap[k].find(toRegexAndMatch)
  })

  return match
}

const groupColumns = (columns: StatusColumn[]) => {
  const groups = columns.reduce<Record<string, StatusColumn[]>>((group, column) => {
    const g = findCategoryFor(column.selectedColumnsDatabaseName)
    if (g) {
      group[g] = group[g] ?? []
      group[g].push(column)
    }
    return group
  }, {})
  const monitoringCategory = groups['Monitoring'] || []
  const dynamicTempMps = columns.filter((c) => {
    return (
      c.selectedColumnsDatabaseName.startsWith('$$tempmp$$') || c.selectedColumnsDatabaseName.startsWith('$$doormp$$')
    )
  })

  groups['Monitoring'] = monitoringCategory.concat(dynamicTempMps)

  return Object.keys(groups)
    .sort()
    .map((k) => {
      return {
        category: k,
        columns: groups[k].sort((a, b) => a.selectedColumnsDisplayName.localeCompare(b.selectedColumnsDisplayName)),
      }
    })
}

const StatusColumnsSelector: FC<StatusColumnsSelectorProps> = ({ onClose }) => {
  const { api } = useApi()
  const log = logger({ category: 'StatusColumnSelector' })

  const { statusColumns, setStatusColumns, resetColumns } = useStatusColumnsContext()
  const { selectedSavedMap } = useMapContext()
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set())
  const [isSaving, setSaving] = useState<boolean>(false)
  const [confirmingReset, setConfirmingReset] = useState<boolean>(false)
  const [isResetting, setResetting] = useState<boolean>(false)

  const groupedColumns = useMemo(() => groupColumns(statusColumns), [statusColumns])

  useEffect(() => {
    setSelectedColumns(new Set(statusColumns.filter((c) => c.selected === 1).map((c) => c.selectedColumnsDatabaseName)))
  }, [statusColumns])

  const hasSelected = (column: string) => selectedColumns.has(column)

  const handleColumnToggle = async (column: StatusColumn) => {
    setSelectedColumns((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(column.selectedColumnsDatabaseName)) {
        newSet.delete(column.selectedColumnsDatabaseName)
      } else {
        newSet.add(column.selectedColumnsDatabaseName)
      }
      return newSet
    })
  }

  const handleReset = async () => {
    try {
      setResetting(true)
      await resetColumns()
      setConfirmingReset(false)
      onClose()
    } catch (e) {
      log.error(e)
    } finally {
      setResetting(false)
    }
  }

  const handleApply = async () => {
    try {
      setSaving(true)
      const added = statusColumns
        .filter((c) => c.selected === 0 && hasSelected(c.selectedColumnsDatabaseName))
        .map((c) => ({ name: c.selectedColumnsDatabaseName, displayOrder: c.displayOrder }))
      const removed = statusColumns
        .filter((c) => c.selected === 1 && !hasSelected(c.selectedColumnsDatabaseName))
        .map((c) => c.selectedColumnsDatabaseName)
      const changedColumns = statusColumns
        .slice()
        .map((c) => ({ ...c, selected: hasSelected(c.selectedColumnsDatabaseName) ? 1 : 0 }))
      // In a saved view, edits are in-memory only and persisted when the user hits Save; writing to
      // the profile would be overridden by the view's column snapshot on the next reload.
      if (!selectedSavedMap) {
        await api.put({
          path: `assets/status-columns`,
          origin: 'StatusColumnsSelector',
          body: { added, removed },
        })
      }
      setStatusColumns(changedColumns)
      onClose()
    } catch (e) {
      log.error(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <dialog className="modal modal-open">
      <div className="modal-box w-350 max-w-350">
        <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" onClick={onClose}>
          ✕
        </button>
        <h3 className="font-bold text-lg">Customize Columns</h3>
        <div className="flex w-full mt-2">
          <div className="flex flex-row w-full border border-gray-200 rounded-xs">
            {groupedColumns.map((g) => (
              <div key={g.category} className="flex flex-1 flex-col border-r last:border-r-0 border-gray-200">
                <div className="flex w-full justify-center items-center p-2 border-b border-gray-200 bg-gray-100 font-semibold">
                  {g.category}
                </div>
                <div className="flex w-full flex-col mt-2 mb-2">
                  {g.columns.map((c) => (
                    <label
                      key={c.id}
                      className="flex w-full justify-start items-center py-1 px-2 gap-2 text-md cursor-pointer hover:font-semibold"
                    >
                      <input
                        type="checkbox"
                        checked={hasSelected(c.selectedColumnsDatabaseName)}
                        onChange={() => handleColumnToggle(c)}
                        className="checkbox checkbox-xs checkbox-primary"
                        placeholder="777"
                      />

                      {c.selectedColumnsDisplayName}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-1 w-full mt-1 justify-between items-center">
          <div className="flex gap-2 items-center">
            {confirmingReset ? (
              <>
                <span className="text-sm text-base-content/70">Reset all column settings to defaults?</span>
                <button className="btn btn-sm btn-error flex gap-1" onClick={handleReset} disabled={isResetting}>
                  {isResetting && <span className="loading loading-spinner loading-sm"></span>}
                  Yes, reset
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => setConfirmingReset(false)}
                  disabled={isResetting}
                >
                  No
                </button>
              </>
            ) : (
              <button
                className="btn btn-sm btn-ghost text-error"
                onClick={() => setConfirmingReset(true)}
                disabled={isSaving}
              >
                Reset to Default
              </button>
            )}
          </div>
          <div className="flex gap-1 items-center">
            <button className="btn btn-default" onClick={onClose} disabled={isSaving || isResetting}>
              Cancel
            </button>
            <button className="btn btn-primary flex gap-1" onClick={handleApply} disabled={isResetting}>
              {isSaving && <span className="loading loading-spinner loading-sm"></span>}
              Apply
            </button>
          </div>
        </div>
      </div>
    </dialog>
  )
}

export default StatusColumnsSelector
